import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { CurrencyEnum } from '@codezo/graphql';

export interface CurrencyInfo {
	code: CurrencyEnum;
	name: string;
	symbol: string;
	flag: string;
}

export const CURRENCY_INFO: Record<CurrencyEnum, CurrencyInfo> = {
	[CurrencyEnum.Usd]: { code: CurrencyEnum.Usd, name: 'US Dollar', symbol: '$', flag: '🇺🇸' },
	[CurrencyEnum.Eur]: { code: CurrencyEnum.Eur, name: 'Euro', symbol: '€', flag: '🇪🇺' },
	[CurrencyEnum.Gbp]: { code: CurrencyEnum.Gbp, name: 'British Pound', symbol: '£', flag: '🇬🇧' },
	[CurrencyEnum.Dkk]: { code: CurrencyEnum.Dkk, name: 'Danish Krone', symbol: 'kr', flag: '🇩🇰' },
	[CurrencyEnum.Sek]: { code: CurrencyEnum.Sek, name: 'Swedish Krona', symbol: 'kr', flag: '🇸🇪' },
	[CurrencyEnum.Nok]: {
		code: CurrencyEnum.Nok,
		name: 'Norwegian Krone',
		symbol: 'kr',
		flag: '🇳🇴',
	},
	[CurrencyEnum.Chf]: { code: CurrencyEnum.Chf, name: 'Swiss Franc', symbol: 'CHF', flag: '🇨🇭' },
	[CurrencyEnum.Cad]: {
		code: CurrencyEnum.Cad,
		name: 'Canadian Dollar',
		symbol: 'C$',
		flag: '🇨🇦',
	},
	[CurrencyEnum.Aud]: {
		code: CurrencyEnum.Aud,
		name: 'Australian Dollar',
		symbol: 'A$',
		flag: '🇦🇺',
	},
	[CurrencyEnum.Jpy]: { code: CurrencyEnum.Jpy, name: 'Japanese Yen', symbol: '¥', flag: '🇯🇵' },
	[CurrencyEnum.Cny]: { code: CurrencyEnum.Cny, name: 'Chinese Yuan', symbol: '¥', flag: '🇨🇳' },
	[CurrencyEnum.Inr]: { code: CurrencyEnum.Inr, name: 'Indian Rupee', symbol: '₹', flag: '🇮🇳' },
	[CurrencyEnum.Krw]: {
		code: CurrencyEnum.Krw,
		name: 'South Korean Won',
		symbol: '₩',
		flag: '🇰🇷',
	},
	[CurrencyEnum.Brl]: {
		code: CurrencyEnum.Brl,
		name: 'Brazilian Real',
		symbol: 'R$',
		flag: '🇧🇷',
	},
	[CurrencyEnum.Mxn]: {
		code: CurrencyEnum.Mxn,
		name: 'Mexican Peso',
		symbol: 'Mex$',
		flag: '🇲🇽',
	},
	[CurrencyEnum.Zar]: {
		code: CurrencyEnum.Zar,
		name: 'South African Rand',
		symbol: 'R',
		flag: '🇿🇦',
	},
	[CurrencyEnum.Try]: { code: CurrencyEnum.Try, name: 'Turkish Lira', symbol: '₺', flag: '🇹🇷' },
	[CurrencyEnum.Hkd]: {
		code: CurrencyEnum.Hkd,
		name: 'Hong Kong Dollar',
		symbol: 'HK$',
		flag: '🇭🇰',
	},
	[CurrencyEnum.Sgd]: {
		code: CurrencyEnum.Sgd,
		name: 'Singapore Dollar',
		symbol: 'S$',
		flag: '🇸🇬',
	},
	[CurrencyEnum.Nzd]: {
		code: CurrencyEnum.Nzd,
		name: 'New Zealand Dollar',
		symbol: 'NZ$',
		flag: '🇳🇿',
	},
	// Other currencies with proper symbols
	[CurrencyEnum.Aed]: { code: CurrencyEnum.Aed, name: 'UAE Dirham', symbol: 'د.إ', flag: '🇦🇪' },
	[CurrencyEnum.Ars]: { code: CurrencyEnum.Ars, name: 'Argentine Peso', symbol: '$', flag: '🇦🇷' },
	[CurrencyEnum.Bgn]: { code: CurrencyEnum.Bgn, name: 'Bulgarian Lev', symbol: 'лв', flag: '🇧🇬' },
	[CurrencyEnum.Clp]: { code: CurrencyEnum.Clp, name: 'Chilean Peso', symbol: '$', flag: '🇨🇱' },
	[CurrencyEnum.Cop]: { code: CurrencyEnum.Cop, name: 'Colombian Peso', symbol: '$', flag: '🇨🇴' },
	[CurrencyEnum.Czk]: { code: CurrencyEnum.Czk, name: 'Czech Koruna', symbol: 'Kč', flag: '🇨🇿' },
	[CurrencyEnum.Egp]: {
		code: CurrencyEnum.Egp,
		name: 'Egyptian Pound',
		symbol: 'ج.م',
		flag: '🇪🇬',
	},
	[CurrencyEnum.Hrk]: { code: CurrencyEnum.Hrk, name: 'Croatian Kuna', symbol: 'kn', flag: '🇭🇷' },
	[CurrencyEnum.Huf]: {
		code: CurrencyEnum.Huf,
		name: 'Hungarian Forint',
		symbol: 'Ft',
		flag: '🇭🇺',
	},
	[CurrencyEnum.Idr]: {
		code: CurrencyEnum.Idr,
		name: 'Indonesian Rupiah',
		symbol: 'Rp',
		flag: '🇮🇩',
	},
	[CurrencyEnum.Ils]: { code: CurrencyEnum.Ils, name: 'Israeli Shekel', symbol: '₪', flag: '🇮🇱' },
	[CurrencyEnum.Isk]: {
		code: CurrencyEnum.Isk,
		name: 'Icelandic Krona',
		symbol: 'kr',
		flag: '🇮🇸',
	},
	[CurrencyEnum.Myr]: {
		code: CurrencyEnum.Myr,
		name: 'Malaysian Ringgit',
		symbol: 'RM',
		flag: '🇲🇾',
	},
	[CurrencyEnum.Pen]: { code: CurrencyEnum.Pen, name: 'Peruvian Sol', symbol: 'S/', flag: '🇵🇪' },
	[CurrencyEnum.Php]: {
		code: CurrencyEnum.Php,
		name: 'Philippine Peso',
		symbol: '₱',
		flag: '🇵🇭',
	},
	[CurrencyEnum.Pln]: { code: CurrencyEnum.Pln, name: 'Polish Zloty', symbol: 'zł', flag: '🇵🇱' },
	[CurrencyEnum.Ron]: { code: CurrencyEnum.Ron, name: 'Romanian Leu', symbol: 'lei', flag: '🇷🇴' },
	[CurrencyEnum.Sar]: { code: CurrencyEnum.Sar, name: 'Saudi Riyal', symbol: 'ر.س', flag: '🇸🇦' },
	[CurrencyEnum.Thb]: { code: CurrencyEnum.Thb, name: 'Thai Baht', symbol: '฿', flag: '🇹🇭' },
	[CurrencyEnum.Vnd]: {
		code: CurrencyEnum.Vnd,
		name: 'Vietnamese Dong',
		symbol: '₫',
		flag: '🇻🇳',
	},
};

interface CurrencyState {
	selectedCurrency: CurrencyEnum;
	setCurrency: (_currency: CurrencyEnum) => void;
	getCurrencyInfo: (_currency: CurrencyEnum) => CurrencyInfo;
}

export const useCurrencyStore = create<CurrencyState>()(
	persist(
		(set) => ({
			selectedCurrency: CurrencyEnum.Usd,
			setCurrency: (currency: CurrencyEnum) => set({ selectedCurrency: currency }),
			getCurrencyInfo: (currency: CurrencyEnum) =>
				CURRENCY_INFO[currency] || CURRENCY_INFO[CurrencyEnum.Usd],
		}),
		{
			name: 'currency-storage',
		}
	)
);
